import { REGEX } from '@admiin-com/ds-common';
import { WBBox, WBFlex, WBTelInputV2, WBTextFieldV2 } from '@admiin-com/ds-web';
import { matchIsValidTel } from 'mui-tel-input';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import AddressForm from '../../features/components/AddressForm/AddressForm';
import { getNestedError } from '../../helpers/string';

export interface ContactFormProps {
  disabled?: boolean;
  prefix?: string;
  isClient?: boolean;
  ocrCreate?: boolean;
  noMobile?: boolean;
  noAddress?: boolean;
  name?: string;
}

export function ContactForm({
  disabled = false,
  isClient = false,
  ocrCreate = false,
  noMobile = false,
  noAddress = false,
  name = 'client',
}: ContactFormProps) {
  const { t } = useTranslation();
  const fieldName = name ? `${name}.` : '';
  const inputs = React.useMemo(
    () => ({
      firstName: {
        label: t('firstName', { ns: 'contacts' }),
        name: `${fieldName}firstName`,
        type: 'text',
        placeholder: t('firstNamePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: ocrCreate
          ? {}
          : { required: t('firstNameRequired', { ns: 'contacts' }) },
      },
      lastName: {
        label: t('lastName', { ns: 'contacts' }),
        name: `${fieldName}lastName`,
        type: 'text',
        placeholder: t('lastNamePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: ocrCreate
          ? {}
          : { required: t('lastNameRequired', { ns: 'contacts' }) },
      },
      email: {
        label: t('email', { ns: 'contacts' }),
        name: `${fieldName}email`,
        type: 'email',
        defaultValue: '',
        placeholder: t('emailPlaceholder', { ns: 'contacts' }),
        rules: ocrCreate
          ? {
              pattern: {
                value: REGEX.EMAIL,
                message: t('invalidEmail', { ns: 'common' }),
              },
            }
          : {
              required: t('emailRequired', { ns: 'common' }),
              pattern: {
                value: REGEX.EMAIL,
                message: t('invalidEmail', { ns: 'common' }),
              },
            },
      },
      mobile: {
        label: t('mobile', { ns: 'contacts' }),
        name: `${fieldName}mobile`,
        type: 'text',
        placeholder: t('mobilePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {
          required: false,
          // required: isClient ? t('mobileRequired', { ns: 'contacts' }) : false,
          validate: (value: string | null | undefined) => {
            console.log('value: ', value);
            return (
              value === null ||
              value === undefined ||
              value === '' ||
              matchIsValidTel(value) ||
              t('invalidMobile', { ns: 'common' })
            );
          },
        },
      },
      phone: {
        label: t('phone', { ns: 'contacts' }),
        name: `${fieldName}phone`,
        type: 'text',
        placeholder: t('phonePlaceholder', { ns: 'contacts' }),
        defaultValue: '',
        rules: {},
      },
      address: {
        label: t(isClient ? 'address' : 'businessAddress', {
          ns: 'onboarding',
        }),
        name: `${fieldName}address`,
        type: 'text',
        placeholder: t('businessAddressPlaceholder', { ns: 'onboarding' }),
        defaultValue: '',
        rules: {},
      },
      postalAddress: {
        label: t(isClient ? 'postalAddress' : 'postalBusinessAddress', {
          ns: 'onboarding',
        }),
        name: `${fieldName}postal`,
        type: 'text',
        placeholder: t('businessAddressPlaceholder', { ns: 'onboarding' }),
        defaultValue: '',
      },
    }),
    [t, isClient, ocrCreate]
  );

  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <>
      <WBFlex flexDirection={['column', 'row']}>
        <WBBox flex={1} pr={[0, 3]}>
          <Controller
            control={control}
            name={inputs.firstName.name}
            rules={inputs.firstName.rules}
            defaultValue={inputs.firstName.defaultValue}
            render={({ field }) => (
              <WBTextFieldV2
                {...field}
                type="text"
                variant="standard"
                label={inputs.firstName.label}
                placeholder={inputs.firstName.placeholder}
                helperText={
                  (getNestedError(errors, field.name)?.message as string) ?? ''
                }
                error={!!getNestedError(errors, field.name)}
                margin="dense"
                disabled={disabled}
              />
            )}
          />
        </WBBox>
        <WBBox flex={1} pl={[0, 3]}>
          <Controller
            control={control}
            name={inputs.lastName.name}
            rules={inputs.lastName.rules}
            defaultValue={inputs.lastName.defaultValue}
            render={({ field }) => (
              <WBTextFieldV2
                {...field}
                type="text"
                variant="standard"
                label={inputs.lastName.label}
                placeholder={inputs.lastName.placeholder}
                error={!!getNestedError(errors, field.name)}
                helperText={
                  (getNestedError(errors, field.name)?.message as string) ?? ''
                }
                margin="dense"
                disabled={disabled}
              />
            )}
          />
        </WBBox>
      </WBFlex>
      <WBFlex flexDirection={['column', 'row']} mt={[0, 4]}>
        <WBBox flex={1} pr={[0, 3]}>
          <Controller
            control={control}
            name={inputs.email.name}
            rules={inputs.email.rules}
            defaultValue={inputs.email.defaultValue}
            render={({ field }) => (
              <WBTextFieldV2
                {...field}
                type="text"
                variant="standard"
                label={inputs.email.label}
                placeholder={inputs.email.placeholder}
                error={!!getNestedError(errors, field.name)}
                helperText={
                  (getNestedError(errors, field.name)?.message as string) ?? ''
                }
                margin="dense"
                disabled={disabled}
              />
            )}
          />
        </WBBox>
        {!noMobile && (
          <WBBox flex={1} pl={[0, 3]}>
            <Controller
              control={control}
              name={inputs.mobile.name}
              defaultValue={inputs.mobile.defaultValue}
              rules={inputs.mobile.rules}
              render={({ field, fieldState }) => (
                //@ts-ignore - value shouldn't be null but is possible by react-form-hooks
                <WBTelInputV2
                  {...field}
                  variant="standard"
                  helperText={
                    fieldState.invalid
                      ? t('invalidPhone', { ns: 'common' })
                      : ''
                  }
                  error={fieldState.invalid}
                  focusOnSelectCountry
                  defaultCountry="AU"
                  label={inputs.mobile.label}
                  margin="dense"
                  disabled={disabled}
                />
              )}
            />
          </WBBox>
        )}
      </WBFlex>

      <WBFlex flexDirection={['column', 'row']} mt={[0, 4]}>
        <WBBox flex={1} pr={[0, 3]}>
          <Controller
            control={control}
            name={inputs.phone.name}
            defaultValue={inputs.phone.defaultValue}
            rules={inputs.phone.rules}
            render={({ field, fieldState }) => (
              <WBTextFieldV2
                {...field}
                type="text"
                variant="standard"
                label={inputs.phone.label}
                placeholder={inputs.phone.placeholder}
                error={!!getNestedError(errors, field.name)}
                helperText={
                  (getNestedError(errors, field.name)?.message as string) ?? ''
                }
                margin="dense"
                disabled={disabled}
              />
            )}
          />
        </WBBox>
      </WBFlex>
      {!isClient && !noAddress && (
        <AddressForm
          label={inputs.address.label}
          name={inputs.address.name}
          placeholder={inputs.address.placeholder}
          onlyAustralia={true}
          optional={true}
          disableManual={false}
        />
      )}
      {!isClient && !noAddress && (
        <AddressForm
          label={inputs.postalAddress.label}
          name={inputs.postalAddress.name}
          placeholder={inputs.postalAddress.placeholder}
          onlyAustralia={true}
          optional={true}
          disableManual={false}
          isPostal
        />
      )}
    </>
  );
}

export default ContactForm;
